import React, { useEffect, useRef, useState } from "react";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, CircularProgress, IconButton, TextField, Typography } from "@mui/material";
import { Extension } from "@tiptap/core";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { TextSelection } from "@tiptap/pm/state";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { LinkBubbleMenu, LinkBubbleMenuHandler, RichTextEditorProvider } from "mui-tiptap";
import type { AICardConfig, AICardData, CardData } from "store/types";

import { FloatingToolbar } from "./FloatingToolbar";

const editorContentSx = {
  width: "100%",
  "& .tiptap": {
    color: "#374151",
    padding: 0,
    outline: "none",
    minHeight: 60,
    fontSize: 13,
    "& p": {
      lineHeight: 1.7,
      margin: 0,
      marginBottom: "8px"
    },
    "& p:last-child": {
      marginBottom: 0
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      lineHeight: 1.4,
      margin: 0,
      marginBottom: "8px"
    },
    "& h1:last-child, & h2:last-child, & h3:last-child, & h4:last-child, & h5:last-child, & h6:last-child": {
      marginBottom: 0
    },
    "& ul, & ol": {
      paddingLeft: "1.5em"
    },
    "& li": {
      marginLeft: "18px"
    },
    "& a:not([data-type='mention'])": {
      color: "#26A6AE !important",
      "&:hover": {
        textDecoration: "underline"
      }
    },
    "& p.is-editor-empty:first-of-type::before": {
      content: "attr(data-placeholder)",
      float: "left",
      height: 0,
      pointerEvents: "none",
      color: "#9eacb5"
    }
  }
};

// Registers a `dir` global attribute on block nodes so the built-in
// setTextDirection command (from @tiptap/core) can persist & render LTR/RTL.
// The official TextDirection extension only registers `dir` when a default
// direction is configured, which would force `dir` onto every exported node;
// here we keep the default null so unset nodes export no `dir`.
const TextDirection = Extension.create({
  name: "textDirection",
  addGlobalAttributes() {
    return [
      {
        types: ["heading", "paragraph"],
        attributes: {
          dir: {
            default: null,
            parseHTML: (element) => element.getAttribute("dir") || null,
            renderHTML: (attributes) => (attributes.dir ? { dir: attributes.dir } : {})
          }
        }
      }
    ];
  }
});

const extensions = [
  StarterKit,
  Placeholder.configure({
    placeholder: "AI answer will appear here...",
    showOnlyCurrent: true
  }),
  TextAlign.configure({
    types: ["heading", "paragraph"]
  }),
  TextDirection,
  LinkBubbleMenuHandler
];

const EditableAnswer: React.FC<{ content: string; onChange?: (html: string) => void }> = ({ content, onChange }) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const revertingRef = useRef(false);

  // The card has a fixed height with overflow hidden. Walk up to that clipping
  // ancestor so we can tell when an edit pushes the content past the block.
  const isContentOverflowing = () => {
    let node = wrapperRef.current?.parentElement ?? null;
    while (node) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "hidden" || overflowY === "auto" || overflowY === "scroll") {
        return node.scrollHeight > node.clientHeight;
      }
      node = node.parentElement;
    }
    return false;
  };

  const editor = useEditor({
    extensions,
    content,
    onUpdate: ({ editor, transaction }) => {
      // Skip the synthetic update produced by our own revert dispatch below.
      if (revertingRef.current) return;

      // If a content change pushed the text past the card's fixed height, undo
      // exactly that change so the user can't type/paste beyond the block.
      if (transaction.docChanged && isContentOverflowing()) {
        revertingRef.current = true;
        try {
          const view = editor.view;
          const before = transaction.before;
          const diff = view.state.doc.content.size - before.content.size;
          const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, before.content);
          const pos = Math.max(1, Math.min(transaction.selection.from - diff, tr.doc.content.size));
          tr.setSelection(TextSelection.near(tr.doc.resolve(pos)));
          tr.setMeta("addToHistory", false);
          view.dispatch(tr);
        } finally {
          revertingRef.current = false;
        }
        return;
      }

      onChangeRef.current?.(editor.getHTML());
    }
  });

  if (!editor) return null;

  return (
    <Box
      ref={wrapperRef}
      sx={{ cursor: "text" }}
      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
    >
      <RichTextEditorProvider editor={editor}>
        <Box sx={editorContentSx}>
          <EditorContent editor={editor} />
        </Box>
        <FloatingToolbar />
        <LinkBubbleMenu />
      </RichTextEditorProvider>
    </Box>
  );
};

const ReadOnlyAnswer: React.FC<{ content: string }> = ({ content }) => {
  const editor = useEditor({
    extensions,
    content,
    editable: false
  });

  if (!editor) return null;

  return (
    <Box sx={editorContentSx}>
      <EditorContent editor={editor} />
    </Box>
  );
};

const QuestionInput: React.FC<{
  question: string;
  loading?: boolean;
  onAsk?: (question: string) => void;
  onTransformToText?: () => void;
}> = ({ question, loading, onAsk, onTransformToText }) => {
  const [draft, setDraft] = useState(question);

  // Keep the draft in sync when the committed question changes externally
  useEffect(() => {
    setDraft(question);
  }, [question]);

  const trimmed = draft.trim();
  const canSend = !!trimmed && trimmed !== question && !loading;

  const submit = () => {
    if (canSend) onAsk?.(trimmed);
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}
      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
    >
      <TextField
        fullWidth
        multiline
        autoFocus={!question.trim()}
        maxRows={4}
        variant="standard"
        value={draft}
        placeholder="Ask AI a question… or press Space to exit"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Space in an empty question field → revert back to a plain text card
          if (e.key === " " && draft.length === 0) {
            e.preventDefault();
            onTransformToText?.();
            return;
          }
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        sx={{ "& .MuiInputBase-input": { fontSize: 13, lineHeight: "20px", color: "#374151" } }}
      />
      <IconButton
        onClick={submit}
        disabled={!canSend}
        title="Ask AI"
        sx={{
          flexShrink: 0,
          width: 28,
          height: 28,
          bgcolor: canSend ? "#1A3C34" : "#E5E7EB",
          color: canSend ? "#FFFFFF" : "#9CA3AF",
          "&:hover": { bgcolor: canSend ? "#142e29" : "#E5E7EB" },
          "&.Mui-disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" }
        }}
      >
        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

interface Props {
  config: AICardConfig;
  data?: CardData;
  loading?: boolean;
  isPreview?: boolean;
  onChange?: (answer: string) => void;
  onAsk?: (question: string) => void;
  onTransformToText?: () => void;
}

export const AICard: React.FC<Props> = ({ config, data, loading, isPreview, onChange, onAsk, onTransformToText }) => {
  const aiData = data as AICardData | undefined;
  const answer = config.answer ?? aiData?.answer;

  // Read-only preview (report export): question heading + answer in a boxed area
  if (isPreview) {
    return (
      <Box sx={{ p: "12px 16px" }}>
        {config.question && (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827", mb: 1.5 }}>{config.question}</Typography>
        )}
        {answer && (
          <Box
            sx={{
              bgcolor: "#F9FAFB",
              borderRadius: "8px",
              p: "12px 16px",
              border: "1px solid #E5E7EB"
            }}
          >
            <ReadOnlyAnswer content={answer} />
          </Box>
        )}
      </Box>
    );
  }

  // Edit mode: question input row on top, answer rendered in the same editor as the text card
  return (
    <Box data-text-content sx={{ p: "12px 16px" }}>
      <QuestionInput question={config.question} loading={loading} onAsk={onAsk} onTransformToText={onTransformToText} />
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
          <CircularProgress size={16} sx={{ color: "#6B7280" }} />
          <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>Getting answer...</Typography>
        </Box>
      ) : answer ? (
        <Box sx={{ mt: 1.5 }}>
          <EditableAnswer content={answer} onChange={onChange} />
        </Box>
      ) : null}
    </Box>
  );
};
